/**
 * The delegation session model: is the signed-in user a team member working inside someone
 * else's account, and whose?
 *
 * ## The token contract (api.core mints it)
 *
 * A team member ("Delegate Support") signs in as themselves, but the token is shaped around the
 * account owner they work for:
 *
 * - `role` is exactly `Delegate Support` — never the owner's roles.
 * - `PartyId` / `ObserverId` are the OWNER's, so every server scope already resolves to the owner.
 * - `ActingForUid`, `ActingFor`, `OwnerRole`, `SupportNo`, `DelegateId`, `ElectionId` (optional)
 *   and `CanSpendCredits` describe the delegation.
 * - `action` holds only the member's own grants.
 *
 * ## The one rule this file exists to keep
 *
 * `OwnerRole` answers "whose data, and what do we call things" — SCOPE and LABELS. It never
 * answers "may this person do X". Gating stays on the `action` claims and the token's real
 * roles (`authority.ts`), which is why `OwnerRole` is never merged into the permission snapshot:
 * a role-gated item such as the owner's Team page must stay hidden from the owner's staff.
 * Callers that branch on a role for scope use `scopeRoles` / `scopeUserId` from here.
 */

import jwtDecode from 'jwt-decode';
import { useSyncExternalStore } from 'react';

import { readStoredEnvelope } from './authority';
import { subscribeToTokenChange } from './token-events';

export const DELEGATE_SUPPORT_ROLE = 'Delegate Support';

export const OWNER_ROLES = ['Candidate', 'Party Admin', 'Observer Admin'] as const;
export type OwnerRole = (typeof OWNER_ROLES)[number];

export interface IDelegation {
	isTeamMember: boolean;
	/** The owner's full name, e.g. "Ada Eze". */
	ownerName: string | null;
	/** The owner's AppUser id, as the token carries it (an int string). */
	ownerUserId: string | null;
	ownerRole: OwnerRole | null;
	/** The member's per-owner number: 2 renders as "S2". */
	supportNumber: number | null;
	delegateId: string | null;
	/** The election the member is locked to, when the owner locked one. */
	electionId: number | null;
	canSpendCredits: boolean;
}

export const NOT_DELEGATED: IDelegation = Object.freeze({
	isTeamMember: false,
	ownerName: null,
	ownerUserId: null,
	ownerRole: null,
	supportNumber: null,
	delegateId: null,
	electionId: null,
	canSpendCredits: false,
});

interface IEnvelope {
	token?: string;
}

type IClaims = Record<string, unknown>;

/** .NET writes a single repeated claim as a bare string and several as an array. */
const toStrings = (value: unknown): string[] => {
	if (typeof value === 'string') return [value];
	if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
	return [];
};

/** The backend serializes unset values as "null"; a claim that says nothing is absent. */
const cleanString = (value: unknown): string | null => {
	if (typeof value !== 'string' && typeof value !== 'number') return null;
	const trimmed = String(value).trim();
	if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
	return trimmed;
};

const positiveInt = (value: unknown): number | null => {
	const text = cleanString(value);
	if (text === null) return null;
	const n = Number(text);
	return Number.isInteger(n) && n > 0 ? n : null;
};

const ownerRoleOf = (value: unknown): OwnerRole | null => {
	const text = cleanString(value)?.toLowerCase();
	return OWNER_ROLES.find((role) => role.toLowerCase() === text) ?? null;
};

const isTrue = (value: unknown): boolean => value === true || cleanString(value)?.toLowerCase() === 'true';

const decode = <T>(token: string): T | null => {
	try {
		return jwtDecode<T>(token);
	} catch {
		return null;
	}
};

/** Pure: delegation facts from already-decoded claims. */
export const delegationFromClaims = (claims: IClaims | null | undefined): IDelegation => {
	if (!claims) return NOT_DELEGATED;
	const roles = toStrings(claims.role).map((r) => r.trim().toLowerCase());
	// The role is the signal. The delegation claims alone, on some other role, are not a session
	// this UI should present as "working for" anyone.
	if (!roles.includes(DELEGATE_SUPPORT_ROLE.toLowerCase())) return NOT_DELEGATED;

	return {
		isTeamMember: true,
		ownerName: cleanString(claims.ActingFor),
		ownerUserId: cleanString(claims.ActingForUid),
		ownerRole: ownerRoleOf(claims.OwnerRole),
		supportNumber: positiveInt(claims.SupportNo),
		delegateId: cleanString(claims.DelegateId),
		electionId: positiveInt(claims.ElectionId),
		canSpendCredits: isTrue(claims.CanSpendCredits),
	};
};

/** Pure: delegation facts from a raw backend token. A token that fails to decode is not delegated. */
export const parseDelegation = (token: string | null | undefined): IDelegation =>
	token ? delegationFromClaims(decode<IClaims>(token)) : NOT_DELEGATED;

let memoKey: string | null = null;
let memoValue: IDelegation = NOT_DELEGATED;

/**
 * Delegation for the signed-in user, read from the stored envelope. Memoized on the raw
 * envelope so `useSyncExternalStore` sees a stable reference.
 */
export const getDelegation = (): IDelegation => {
	const envelope = readStoredEnvelope();
	if (memoKey === envelope) return memoValue;
	memoKey = envelope;
	memoValue = envelope ? parseDelegation(decode<IEnvelope>(envelope)?.token) : NOT_DELEGATED;
	return memoValue;
};

/** Test seam — drops the memo so a test can swap the stored token. */
export const resetDelegationCache = () => {
	memoKey = null;
	memoValue = NOT_DELEGATED;
};

const getServerSnapshot = (): IDelegation => NOT_DELEGATED;

/** Live delegation state; re-reads when `extend-session` rotates the token. */
export const useDelegation = (): IDelegation => useSyncExternalStore(subscribeToTokenChange, getDelegation, getServerSnapshot);

/**
 * Roles to use when deciding SCOPE or LABELS — the token's roles plus, for a team member, the
 * owner's role. Never pass the result to a permission check.
 */
export const scopeRoles = (roles: readonly string[], delegation: IDelegation = getDelegation()): string[] =>
	delegation.isTeamMember && delegation.ownerRole && !roles.includes(delegation.ownerRole)
		? [...roles, delegation.ownerRole]
		: [...roles];

/** True when `role` names the account whose data is shown — the user's own role, or a team member's owner. */
export const actsAsRole = (roles: readonly string[], role: OwnerRole, delegation: IDelegation = getDelegation()): boolean =>
	scopeRoles(roles, delegation).includes(role);

/** Whose AppUser id scopes a candidate-keyed lookup: the owner's for a team member, else the caller's. */
export const scopeUserId = (ownUserId: string | null | undefined, delegation: IDelegation = getDelegation()): string =>
	(delegation.isTeamMember ? delegation.ownerUserId : null) ?? ownUserId ?? '';
